import { useTheme } from '@material-ui/core/styles';
import { Options } from 'highcharts/highstock';
import { ToISODateOptions } from 'luxon';
import React, { useCallback, useEffect, useState, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartData, FlamingoFund, UnitCategory, UnitCategoryCode, isFundVisibleForUnitCategory } from 'funds-model';
import {
  getDateTimeFromString,
  formatIsoToStandardDate,
} from 'common-react/src/utils/dateUtils';
import { formatIsoToDotDate } from 'common-react/src/utils/formatters';
import { ComponentErrorBoundaryState } from 'common-react/src/error/ComponentErrorBoundary';
import { getShortMonths } from './chartsUtils';
import { useChartDates } from './useChartDates';
import { makeChangeRanges } from './fundBigChartChangeRanges';
import FundBigChartError from './FundBigChartError';
import { buildOptions } from './FundBigChartOptions';
import FundBigChartPricingBox from './FundBigChartPricingBox';
import { AlertBox } from '../AlertBox/AlertBox';
import { Dropdown } from '../form/Dropdown/Dropdown';
import { toDropdownTranslateOptions } from '../form/Dropdown/tools/Dropdown.utils';
import { MultiButton } from '../form/MultiButton/MultiButton';
import { useIsMobile } from '../utils/useIsMobile';
import { LabelWithTooltip } from '../LabelWithTooltip/LabelWithTooltip';
import fundListOptions from '../FundList/fundListOptions';
import { zIndex, getFundColors } from '../theme/constants';
import { useHighchartsSharedSetup } from '../utils/useHighchartsSharedSetup';
import { useHiddenSeriesNames } from '../utils/useHiddenSeriesNames';
import { ChartDateRangeControls } from '../ChartDateRangeControls/ChartDateRangeControls';
import { StockChartWithBoundary } from '../StockChartWithBoundary/StockChartWithBoundary';
import clsx from 'clsx';
import 'highcharts/modules/accessibility';

import useStyles from './FundBigChart.style';

export enum ChartType {
  ROI_CHART_TYPE = 'roi',
  PRICING_CHART_TYPE = 'pricing'
}

export interface FundBigChartProps {
  funds: FlamingoFund[],
  unitCategoryCode: string,
  unitCategory?: UnitCategory,
  embeddedInFundPopup?: boolean,
  changeRateKey?: string,
  onUnitCategoryCodeChange?: (unitCategoryCode: UnitCategoryCode) => void;
  hasDynamicUnitCategoryCode?: boolean;
  getFundsChartDataPromise: (fromDate: string, endDate: string | ((options?: ToISODateOptions) => string), unitCategoryCode: string, chartType: ChartType) => Promise<ChartData[]>,
  lastFundDate?: string,
  renderChartTypeButtonsPlacement?: 'top' | 'bottom',
}
const areUnitCategoryPropsEqual = (oldProps: FundBigChartProps, newProps: FundBigChartProps) =>
    oldProps?.unitCategoryCode === newProps?.unitCategoryCode &&
    oldProps?.unitCategory === newProps?.unitCategory &&
    oldProps?.hasDynamicUnitCategoryCode === newProps?.hasDynamicUnitCategoryCode &&
    oldProps?.onUnitCategoryCodeChange === newProps?.onUnitCategoryCodeChange;

const arePropsEqual = (oldProps: FundBigChartProps, newProps: FundBigChartProps) => {
  return (
      areUnitCategoryPropsEqual(oldProps, newProps) &&
      oldProps?.embeddedInFundPopup === newProps?.embeddedInFundPopup &&
      oldProps?.changeRateKey === newProps?.changeRateKey &&
      oldProps?.funds.every((oldFund, index) => {
        const newFund = newProps.funds[index];
        return oldFund.id === newFund.id && oldFund.name === newFund.name;
      }) && oldProps.lastFundDate === newProps.lastFundDate
  );
}

const FundBigChart: React.FC<FundBigChartProps> = memo(({
                                                          funds, unitCategoryCode, embeddedInFundPopup = false, unitCategory, changeRateKey, onUnitCategoryCodeChange, hasDynamicUnitCategoryCode,
                                                          getFundsChartDataPromise, lastFundDate, renderChartTypeButtonsPlacement = 'top',
                                                        }) => {
  const [t] = useTranslation('components');
  const [tt, i18n] = useTranslation('translation');
  const [tComponents] = useTranslation('components');
  const theme = useTheme();
  const isDarkTheme = theme.variables.isDarkTheme;
  const isMobile = useIsMobile();
  const c = useStyles({ embeddedInFundPopup, isDarkTheme });

  const [chartType, setChartType] = useState(ChartType.ROI_CHART_TYPE);
  const [chartMetadata, setChartMetadata] = useState({change: 0, firstValue: 0, lastValue: 0, firstDate: '', lastDate: '', selectedRange: ''});
  const [options, setOptions] = useState<Options | null>(null);
  const shortMonths = useMemo(() => getShortMonths(tt), [tt]);
  const {
    hiddenSeriesNamesRef,
    handleLegendVisibilityChange,
    pruneHiddenSeriesNames,
  } = useHiddenSeriesNames();

  const getUnitCategoryLabel = () => fundListOptions.unitCategoryCode.find(option => option.code === unitCategoryCode)?.label;
  const [unitCategoryValue, setUnitCategoryValue] = useState(getUnitCategoryLabel());
  const [dynamicUnitCategoryCode, setDynamicUnitCategoryCode] = useState<string | undefined>(unitCategoryCode);
  const currentUnitCategoryCode = dynamicUnitCategoryCode || unitCategoryCode;

  useHighchartsSharedSetup({
    shortMonths,
    proxyPositionResetTrigger: options,
  });

  useEffect(() => onUnitCategoryCodeChange?.(currentUnitCategoryCode as UnitCategoryCode),
      [currentUnitCategoryCode, onUnitCategoryCodeChange]);

  const {
    fromDate,
    endDate,
    lastDate,
    changeRange,
    handleDateRangeChange,
    handleNavigatorChange,
    handleFromDateChange,
    handleEndDateChange,
    setFromDate,
    setEndDate,
    setIsReadyForChartApiCall,
    isReadyForChartApiCall,
    errorMessage,
  } = useChartDates(useCallback(date => makeChangeRanges(tt, date), [tt]),
      0,
      changeRateKey,
      lastFundDate,
  );

  const handleChartTypeChange = (type: ChartType) => {
    setChartType(type);
    setIsReadyForChartApiCall(true);
  };

  useEffect(() => {
    let subscribed = true;
    if (!isReadyForChartApiCall) {
      return;
    }

    const fundsColor = getFundColors(theme);
    const fundsNameWithColor = funds
        .filter(fund => fund != null)
        .map((fund, i) => ({ name: fund.name, color: embeddedInFundPopup ? theme.color.violets[100] : fundsColor[i] }));

    getFundsChartDataPromise(fromDate, endDate, currentUnitCategoryCode, chartType).then(responses => {
      const chartDataList = responses.map(response => response.data);

      if (chartDataList.length < 1) {
        if (subscribed) {
          setOptions(null);
        }
        return;
      }

      if (embeddedInFundPopup) {
        const {change, firstValue, lastValue, firstDate, lastDate: quoteLastDate} = responses[0];
        if (subscribed) {
          setFromDate(firstDate);
          setEndDate(quoteLastDate);
          setChartMetadata({
            change,
            firstValue,
            lastValue,
            firstDate: formatIsoToDotDate(firstDate),
            lastDate: formatIsoToDotDate(quoteLastDate),
            selectedRange: changeRange?.key || ''
          });
        }
      } else {
        //find min firstDate and max lastDate from all responses
        const firstDates = responses.map(response => response.firstDate);

        const lastDates = responses.map(response => response.lastDate);
        const minFirstDate = firstDates.reduce((min, date) => date < min ? date : min, firstDates[0]);
        const maxLastDate = lastDates.reduce((max, date) => date > max ? date : max, lastDates[0]);
        if (subscribed) {
          setFromDate(minFirstDate);
          setEndDate(maxLastDate);
        }
      }

      const chartOptions = buildOptions(chartDataList,
          i18n.language,
          chartType,
          fundsNameWithColor,
          handleNavigatorChange,
          theme,
          isMobile,
          embeddedInFundPopup,
          isDarkTheme,
          hiddenSeriesNamesRef.current,
          handleLegendVisibilityChange);
      if (subscribed) {
        setOptions(chartOptions);
      }
    });

    return () => {
      subscribed = false;
    };
  }, [
    chartType,
    funds,
    fromDate,
    endDate,
    setFromDate,
    setEndDate,
    currentUnitCategoryCode,
    i18n.language,
    theme,
    isMobile,
    embeddedInFundPopup,
    isDarkTheme,
    handleNavigatorChange,
    hiddenSeriesNamesRef,
    isReadyForChartApiCall,
    lastFundDate,
    changeRange?.key
  ]);

  useEffect(() => {
    pruneHiddenSeriesNames(funds.map(fund => fund.name));
  }, [funds, pruneHiddenSeriesNames]);

  const lastDateFormatted = formatIsoToDotDate(lastDate);

  const isFromDateOlderThanOldestQuote = () => {
    const oldestQuoteDate = funds.length === 1 && funds[0]?.oldestQuotesForUnitCode[currentUnitCategoryCode]?.date;
    const dateToCompare = changeRange?.fromDate ?? fromDate;
    return dateToCompare && oldestQuoteDate &&
        getDateTimeFromString(dateToCompare).startOf('day') < getDateTimeFromString(oldestQuoteDate).startOf('day');
  }
  const renderChartMetadataDesktopSection = () => (
      <>
        {!embeddedInFundPopup &&
            <div className={c.info}>
              {`${t('fundChart.valuation-day')} `}
              <span className={c.infoDark}>{lastDateFormatted}</span>
              {` • ${t('fundChart.share-class')}: `}
              <span className={c.infoDark}>{currentUnitCategoryCode}</span>
            </div>}
        {embeddedInFundPopup && <FundBigChartPricingBox chartMetadata={chartMetadata} />}
      </>
  );

  const renderChartMetadataMobileSection = () => (
      <>
        {!embeddedInFundPopup &&
            <div className={c.info}>
              <div>{`${t('fundChart.valuation-day')} `}<span className={c.infoDark}>{lastDateFormatted}</span></div>
              <div>{`${t('fundChart.share-class')}: `}<span className={c.infoDark}>{currentUnitCategoryCode}</span></div>
            </div>
        }
        {embeddedInFundPopup && <FundBigChartPricingBox chartMetadata={chartMetadata} />}
      </>
  );

  const fundListOptionsUnitCategoryFiltered = fundListOptions.unitCategoryCode
                                                             .filter(option => isFundVisibleForUnitCategory(funds[0], option.code))
                                                             .map(option => option.label);

  const unitCategoryDropdownOptions = toDropdownTranslateOptions(
      fundListOptions.unitCategoryCode.map(option => option.label),'fund-list.options.unit-category', tComponents
  ).map(option =>
      fundListOptionsUnitCategoryFiltered.includes(option.value) ? option : { ...option, disabled: true }
  );

  const categoryUnitBoxDisabled = useMemo(
    () =>
      !hasDynamicUnitCategoryCode ||
      fundListOptionsUnitCategoryFiltered.length === 1,
    [hasDynamicUnitCategoryCode, fundListOptionsUnitCategoryFiltered]
  );

  const { tooltipAppBarZIndex } = zIndex;

  const renderCategoryUnitBox = () => (
      <div className={clsx(c.categoryUnitBox, { [c.categoryUnitBoxDisabled]: categoryUnitBoxDisabled })}>
        <LabelWithTooltip
          customZIndex={tooltipAppBarZIndex}
          labelText={t('fundChart.share-class')}
          preTitle={isMobile ? t('fundChart.share-class') : ''}
          title={t('fundChart.unit-category-tooltip')}
          color="graphite"
          size="s"
          className={c.categoryUnitLabel}
        />
        {categoryUnitBoxDisabled && <div className={c.categoryUnitValue}>
          {unitCategory != null ? `${currentUnitCategoryCode} - ${unitCategory.description}` : currentUnitCategoryCode}
        </div>}
        {(hasDynamicUnitCategoryCode && fundListOptionsUnitCategoryFiltered.length > 1) && <Dropdown
            onChange={event => {
              const unitCategoryCodeFromEvent = fundListOptions.unitCategoryCode.find(option => option.label === event.target.value )?.code;
              setUnitCategoryValue(event.target.value as string);
              setDynamicUnitCategoryCode(unitCategoryCodeFromEvent);
              setIsReadyForChartApiCall(true);
            }}
            options={unitCategoryDropdownOptions}
            value={unitCategoryValue as string}
            listTitle={tComponents('fund-list.unit-category-of-participation')}
            placeholderDisplayType={'dynamic' as const}
            placeholder={tComponents('fund-list.unit-category-of-participation')}
            className={c.unitCategoryDropdown}
        />}
      </div>
  );

  const renderChartTypeButtons = () => (
      <MultiButton
          options={[
            { label: t('fundChart.roi-chart-type'), value: ChartType.ROI_CHART_TYPE },
            { label: t('fundChart.pricing-chart-type'), value: ChartType.PRICING_CHART_TYPE }
          ]}
          getCheckedValue={value => chartType === value}
          onChange={e => handleChartTypeChange(e.target.value as ChartType)}
          radioGroupName="chart-type-select"
      />
  );

  const renderChartTypeButtonsWithMetaData = () => {
    return (
      <div className={c.panelFooter}>
        <div className={c.buttonsBox}>{renderChartTypeButtons()}</div>
        {!embeddedInFundPopup &&
          !isMobile &&
          renderChartMetadataDesktopSection()}
      </div>
    );
  };

  const fallbackErrorComponent = (errorState: ComponentErrorBoundaryState, clearState: () => void) =>
      <FundBigChartError errorState={errorState} clearState={clearState} />;

  return (
      <>
        <div>
        {embeddedInFundPopup && <div className={c.firstRow}>
            {renderChartMetadataDesktopSection()}
            {renderCategoryUnitBox()}
          </div>}
          <div className={c.secondRow}>
            {renderChartTypeButtonsPlacement === 'top' && renderChartTypeButtonsWithMetaData()}
            <div className={clsx({
              [c.timeRangesPanel]: true,
              [c.timeRangesPanelBottom]: renderChartTypeButtonsPlacement === 'bottom',
            })}>
              <ChartDateRangeControls
                  rangeKeys={makeChangeRanges(tt).map(obj => obj.key)}
                  selectedRangeKey={changeRange?.key}
                  onRangeChange={handleDateRangeChange}
                  radioGroupName="chart-date-range-select"
                  fromDateValue={fromDate}
                  fromDateErrorMessage={errorMessage.fromDate ? t(`fundChart.${errorMessage.fromDate}`, {
                    lastDate: formatIsoToStandardDate(lastDate),
                  }) : undefined}
                  onFromDateChange={handleFromDateChange}
                  toDateValue={endDate}
                  toDateErrorMessage={errorMessage.endDate ? t(`fundChart.${errorMessage.endDate}`, {
                    lastDate: formatIsoToStandardDate(lastDate),
                  }) : undefined}
                  onToDateChange={handleEndDateChange}
                  maxDate={lastDate}
              />
            </div>
            {renderChartTypeButtonsPlacement === 'bottom' && renderChartTypeButtonsWithMetaData()}
          </div>
        </div>

        {isFromDateOlderThanOldestQuote() &&
            <AlertBox
                className={c.alertBox}
                colorStyle='blue'
                title={t('fundChart.chart-show-all',
                    { oldestQuotesForUnitCode: formatIsoToStandardDate(funds[0]?.oldestQuotesForUnitCode[currentUnitCategoryCode]?.date) })}
            />}

        <div className={c.chartWrapper}>
          <StockChartWithBoundary options={options} fallbackErrorComponent={fallbackErrorComponent} />
          {isMobile && !embeddedInFundPopup && renderChartMetadataMobileSection()}
        </div>
      </>
  );
}, arePropsEqual);

export default FundBigChart;
